// ─────────────────────────────────────────────────────────────────────────────
// utils/jobPagination.ts — Blaettern statt Kuerzen in der Auftragsliste (T-M9.3)
//
// REQ-OPS-033 (die Auftragsliste kennt KEINE feste Obergrenze mehr, sondern
// laedt nach), REQ-DM-050/050a (deterministische, stabile Reihenfolge samt
// Sortierrichtung), REQ-PS-021 (Live-Ueberwachung ist Kernzweck des Dashboards).
//
// Warum es diese Datei gibt — der Befund, gegen den gebaut wird:
// Die Abfrage sortierte `createdAt` ABSTEIGEND und kappte bei 200. Gekappt
// werden damit die AELTESTEN Dokumente — also genau die in `pending` haengenden
// Auftraege eines gestauten Druckers. Im Stoerfall, fuer den das Dashboard
// gebaut ist, blendete die Ansicht damit das Wichtigste aus. Planungsgroesse
// sind 200 gestaute Arbeitsbons EINER Warteschlange (Payfix 2026-07-30).
//
// Die Regeln liegen bewusst als REINE Funktionen ohne Vue- und
// Firestore-Abhaengigkeit hier, nicht im Store: die Seitengrenze ist die
// Stelle, an der Doppel- und Fehlanzeigen entstehen, und sie muss ohne Mount
// pruefbar sein (Vorbild: utils/screenChannelJobs.ts, T-M9.9).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Seitengroesse: 50 Auftraege je Seite.
 *
 * Herleitung (nicht gewaehlt, sondern abgeleitet — REQ-OPS-033): die
 * Planungsgroesse sind 200 gestaute Arbeitsbons EINER Druckerwarteschlange
 * (Payfix, 2026-07-30, Rueckfrage 2b). Mit 50 sind sie in vier Schritten
 * vollstaendig erreichbar, und die erste Seite bleibt klein genug, dass die
 * Ansicht sofort steht. Der Wert darf mit dokumentierter Begruendung geaendert
 * werden; er ist eine benannte Konstante und keine anonyme Zahl in der Abfrage.
 *
 * Das ist AUSDRUECKLICH keine Kuerzungsgrenze: jenseits der ersten Seite wird
 * nachgeladen, nicht abgeschnitten. Die frueher hier wirkende stille Grenze
 * `MAX_JOBS_LIMIT = 200` gibt es nicht mehr.
 */
export const JOBS_PAGE_SIZE = 50;

/** Der fuer die Seitenlogik gelesene Teil eines `printJobs`-Dokuments. */
export interface PagedJobLike {
  id: string;
  /**
   * Eingangszeit (Firestore-`Timestamp`). Fehlt sie, gilt der Auftrag als
   * aeltest moeglich — Firestore liefert Dokumente ohne das Sortierfeld
   * ohnehin nicht aus, und der Lesepfad verwirft sie zusaetzlich.
   */
  createdAt?: { seconds?: number; nanoseconds?: number } | null;
}

function seconds(job: PagedJobLike): number {
  return job.createdAt?.seconds ?? 0;
}

function nanos(job: PagedJobLike): number {
  return job.createdAt?.nanoseconds ?? 0;
}

/**
 * Vergleich in der Reihenfolge der Abfrage: `createdAt` ABSTEIGEND, danach die
 * Dokument-ID ABSTEIGEND.
 *
 * Der zweite Schluessel ist kein Zusatz, sondern Firestores eigener Tiebreak:
 * eine absteigend nach `createdAt` sortierte Abfrage sortiert bei Gleichstand
 * implizit nach `__name__` in DERSELBEN Richtung. Wer clientseitig anders
 * sortiert, verschiebt die Seitengrenze gegenueber dem Cursor — und genau dort
 * entstehen Doppel- und Fehlanzeigen (REQ-DM-050a).
 */
export function compareJobsByQueryOrder(a: PagedJobLike, b: PagedJobLike): number {
  const bySeconds = seconds(b) - seconds(a);
  if (bySeconds !== 0) return bySeconds;
  const byNanos = nanos(b) - nanos(a);
  if (byNanos !== 0) return byNanos;
  if (a.id === b.id) return 0;
  return a.id < b.id ? 1 : -1;   // ID absteigend
}

/**
 * Ist `job` in der Abfragereihenfolge AELTER als `reference` (steht also weiter
 * unten in der Liste)?
 */
export function isOlderThan(job: PagedJobLike, reference: PagedJobLike): boolean {
  return compareJobsByQueryOrder(job, reference) > 0;
}

/**
 * Die anzuzeigende Liste aus den drei Quellen.
 *
 *   • `livePage`     — die erste Seite, per `onSnapshot` LAUFEND aktualisiert.
 *   • `staticPages`  — nachgeladene Seiten (Cursor-Abfragen), sie stehen still.
 *   • `retained`     — Zeilen, die unter die Live-Seite abgesunken sind
 *                      (siehe `retainedAfterLiveUpdate`).
 *
 * Doppelte Kennungen sind moeglich und werden hier aufgeloest — die Live-Seite
 * gewinnt, danach die frueher geladene Seite. Ohne Aufloesung erschiene ein
 * Auftrag zweimal, sobald sich die Live-Seite ueber eine nachgeladene Seite
 * schiebt (z. B. weil oben Auftraege geloescht wurden).
 *
 * Sortiert wird ueber ALLE Quellen hinweg nach der Abfragereihenfolge, nicht
 * bloss angehaengt: nur so bleibt die Reihenfolge ueber Seitengrenzen hinweg
 * stabil, auch wenn waehrend des Blaetterns Auftraege eintreffen.
 */
export function mergeJobPages<T extends PagedJobLike>(
  livePage: readonly T[],
  staticPages: readonly (readonly T[])[] = [],
  retained: readonly T[] = [],
): T[] {
  const byId = new Map<string, T>();
  for (const job of [...livePage, ...staticPages.flat(), ...retained]) {
    if (!byId.has(job.id)) byId.set(job.id, job);
  }
  return [...byId.values()].sort(compareJobsByQueryOrder);
}

/**
 * Welche bereits angezeigten Zeilen sind nach einer Live-Aktualisierung zu
 * BEHALTEN, obwohl sie nicht mehr in der Live-Seite stehen?
 *
 * Das Problem, das damit geschlossen wird — die einzige echte Luecke dieses
 * Modells: die nachgeladenen Seiten haengen an einem Cursor (`startAfter` auf
 * dem damals letzten Dokument der Live-Seite). Treffen danach neue Auftraege
 * ein, rutscht die Live-Seite nach oben; ihr damals letztes Dokument steht dann
 * WEDER in der Live-Seite NOCH in Seite 2 — es waere eine Luecke mitten in der
 * Liste. Solche abgesunkenen Zeilen werden deshalb weitergefuehrt.
 *
 * Sie sind ab diesem Moment STATISCH — dieselbe Eigenschaft wie eine
 * nachgeladene Seite, und aus demselben Grund: live bleibt ausschliesslich die
 * erste Seite.
 *
 * Zwei Bedingungen verhindern, dass daraus Karteileichen werden:
 *   1. Nur wenn ueberhaupt nachgeladen wurde (`staticPages` nicht leer). Ohne
 *      nachgeladene Seiten gibt es keine Luecke, die zu schliessen waere.
 *   2. Nur Zeilen UNTERHALB des letzten Dokuments der Live-Seite. Ein GELOESCHTER
 *      Auftrag innerhalb des Live-Fensters liegt nicht darunter und verschwindet
 *      damit richtigerweise; nur die unten hinausgeschobenen bleiben.
 * Eine nicht volle Live-Seite kann nichts hinausschieben — dann wird ebenfalls
 * nichts behalten.
 */
export function retainedAfterLiveUpdate<T extends PagedJobLike>(
  previouslyDisplayed: readonly T[],
  livePage: readonly T[],
  staticPages: readonly (readonly T[])[],
  pageSize: number = JOBS_PAGE_SIZE,
): T[] {
  if (staticPages.length === 0) return [];
  if (livePage.length < pageSize) return [];

  const tail = livePage[livePage.length - 1];
  if (!tail) return [];

  const known = new Set<string>();
  for (const job of [...livePage, ...staticPages.flat()]) known.add(job.id);

  return previouslyDisplayed.filter(job => !known.has(job.id) && isOlderThan(job, tail));
}

/**
 * Kann noch weiter nachgeladen werden?
 *
 * Massgeblich ist die zuletzt GELADENE Seite: eine volle Seite bedeutet, dass
 * dahinter weitere Auftraege liegen koennen; eine kurze Seite ist das Ende der
 * Liste. Solange nichts nachgeladen wurde (`lastPageSize === null`), entscheidet
 * die Live-Seite.
 *
 * Bewusst grosszuegig: bei einer exakt aufgehenden Gesamtzahl (z. B. genau 100
 * Auftraege) meldet die Funktion einmal zu viel „es gibt mehr". Der naechste
 * Ladeversuch liefert dann null Auftraege und beendet die Liste sichtbar. Der
 * umgekehrte Fehler waere schlimmer: eine still verschwiegene letzte Seite.
 */
export function canLoadMore(
  livePageSize: number,
  lastPageSize: number | null,
  pageSize: number = JOBS_PAGE_SIZE,
): boolean {
  return (lastPageSize ?? livePageSize) >= pageSize;
}
